import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Sparkles, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL ?? ''}/api`;

const YELLOW = "#FFBD00";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: "easeOut" },
};

const MosaicStrip = () => (
  <div className="overflow-hidden border-y border-white/20 bg-black" data-testid="mosaic-strip">
    <div className="marquee-track">
      {[...Array(8)].map((_, i) => (
        <img
          key={i}
          src="/img/mosaic-strip.jpg"
          alt="Norman Seeff sessions — iconic portraits"
          className="h-40 md:h-56 w-auto border-r border-white/30 select-none"
          draggable="false"
        />
      ))}
    </div>
  </div>
);

const DetailItem = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-[#262626] bg-[#0C0C0C] p-6 md:p-8" data-testid={testId}>
    <Icon size={20} style={{ color: YELLOW }} />
    <div className="mt-4 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">{label}</div>
    <div className="mt-2 text-lg font-normal uppercase tracking-wide text-white md:text-xl whitespace-pre-line">
      {value}
    </div>
  </div>
);

const Ticket = ({ ticket }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
    className="mx-auto max-w-lg border border-[#FFBD00] bg-[#0C0C0C]"
    data-testid="rsvp-confirmation-ticket"
  >
    <div className="border-b border-[#262626] px-8 py-5 flex items-center justify-between">
      <span className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: YELLOW }}>
        {ticket.already_registered ? "Already Registered" : "RSVP Confirmed"}
      </span>
      <span className="text-[11px] uppercase tracking-[0.2em] text-[#A3A3A3]">Admit {ticket.guests}</span>
    </div>
    <div className="px-8 py-8 text-left">
      <div className="font-display text-2xl font-semibold uppercase tracking-wider text-white" data-testid="ticket-guest-name">
        {ticket.name}
      </div>
      <div className="mt-1 text-sm text-[#A3A3A3]">{ticket.email}</div>
      <div className="mt-8 flex items-center gap-6">
        <img
          src={ticket.qr_code}
          alt="Check-in QR code"
          className="h-40 w-40 border-4 border-white"
          data-testid="ticket-qr-code"
        />
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Check-in Code</div>
          <div className="mt-1 font-display text-2xl tracking-[0.2em]" style={{ color: YELLOW }} data-testid="ticket-checkin-code">
            {ticket.checkin_code}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-[#A3A3A3]">
            Present this QR code at the door on July 1st. Save a screenshot — a copy will also be emailed to you.
          </p>
        </div>
      </div>
    </div>
    <div className="border-t border-[#262626] px-8 py-4 text-[11px] uppercase tracking-[0.2em] text-[#A3A3A3]">
      Yamashiro, Hollywood — July 1st, 2026 — Program Starts at 8pm Sharp (or No Admittance after 8pm)
    </div>
  </motion.div>
);

const HONEYPOT_FIELDS = ["website", "company", "fax_number", "middle_name",
  "address2", "nickname", "phone_secondary", "business_email"];

const RsvpForm = ({ onSuccess }) => {
  const [form, setForm] = useState({ name: "", email: "", guests: 1, notes: "" });
  const [honeypots, setHoneypots] = useState(
    Object.fromEntries(HONEYPOT_FIELDS.map((f) => [f, ""]))
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/rsvp`, {
        ...form,
        guests: Number(form.guests),
        ...honeypots,
      });
      onSuccess(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" ")
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#333] px-0 py-3 text-white outline-none transition-colors focus:border-[#FFBD00] placeholder:text-[#555]";

  return (
    <form onSubmit={submit} autoComplete="off" className="relative mx-auto max-w-xl text-left" data-testid="rsvp-form">
      {/* Honeypot fields — invisible to humans, irresistible to bots */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, width: 0, height: 0, overflow: "hidden" }}>
        {HONEYPOT_FIELDS.map((name) => (
          <input
            key={name}
            type="text"
            name={name}
            tabIndex={-1}
            autoComplete="off"
            value={honeypots[name]}
            onChange={(e) => setHoneypots({ ...honeypots, [name]: e.target.value })}
            placeholder={name.replace("_", " ")}
          />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: YELLOW }}>
            Full Name *
          </label>
          <input required value={form.name} onChange={set("name")} placeholder="Your name"
            className={inputCls} data-testid="rsvp-name-input" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: YELLOW }}>
            Email *
          </label>
          <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com"
            className={inputCls} data-testid="rsvp-email-input" />
        </div>
      </div>
      <div className="mt-8">
        <label className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: YELLOW }}>
          Number of Guests (including you) *
        </label>
        <input required type="number" min="1" max="20" value={form.guests} onChange={set("guests")}
          className={inputCls} data-testid="rsvp-guests-input" />
      </div>
      <div className="mt-8">
        <label className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: YELLOW }}>
          Notes
        </label>
        <textarea rows={3} value={form.notes} onChange={set("notes")}
          placeholder="Dietary requirements, plus-one names, anything we should know…"
          className={`${inputCls} resize-none`} data-testid="rsvp-notes-input" />
      </div>
      {error && (
        <p className="mt-6 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="rsvp-error-message">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="mt-10 w-full bg-[#FFBD00] px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white disabled:opacity-50"
        data-testid="rsvp-submit-button"
      >
        {submitting ? "Submitting…" : "Confirm Attendance"}
      </button>
    </form>
  );
};

const FlyerModal = ({ src, alt, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
    onClick={onClose}
    data-testid="flyer-lightbox"
  >
    <button
      onClick={onClose}
      className="absolute right-6 top-6 text-3xl text-white hover:text-[#FFBD00] transition-colors"
      aria-label="Close"
    >
      ✕
    </button>
    <img
      src={src}
      alt={alt}
      className="max-h-[90vh] max-w-full border border-white/20 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    />
  </div>
);

export default function Landing() {
  const [ticket, setTicket] = useState(null);
  const [showFlyer, setShowFlyer] = useState(false);

  const scrollToRsvp = () => document.getElementById("rsvp")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen text-white">
      {/* Mosaic background of Norman's photos with overlay */}
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: "url('/img/mosaic-bg.jpg')",
          backgroundSize: "880px auto",
          backgroundRepeat: "repeat",
        }}
        data-testid="mosaic-background"
      />
      <div className="fixed inset-0 bg-[#050505]/[0.93]" />

      <div className="relative z-10">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-[#1A1A1A] px-6 py-4 md:px-12">
        <span className="font-title text-sm uppercase tracking-[0.3em]">Norman Seeff</span>
        <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: YELLOW }}>
          By Invitation Only
        </span>
      </div>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 pb-20 pt-16 md:px-12 md:pt-24">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="font-title text-xs uppercase tracking-[0.35em] text-white">
              The <span style={{ color: YELLOW }}>Power</span> &amp; The <span style={{ color: YELLOW }}>Passion</span> to Create
            </div>
            <h1 className="mt-6 font-title text-5xl uppercase leading-[0.95] tracking-wide sm:text-6xl lg:text-7xl" style={{ color: YELLOW }}>
              The Sessions
              <br />
              Project
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-[#A3A3A3]">
              A cultural event 50 years in the making. Since 1975, Norman Seeff has been documenting the
              world&apos;s greatest artists, innovators, and cultural leaders in a setting of his own
              invention — <span className="text-white">The Session</span>.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              <button
                onClick={scrollToRsvp}
                className="bg-[#FFBD00] px-10 py-4 font-display text-sm font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white"
                data-testid="hero-rsvp-button"
              >
                RSVP Now
              </button>
              <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                July 1st, 2026 — Program Starts at 8pm Sharp (or No Admittance after 8pm) — Yamashiro, Hollywood
              </div>
            </div>
          </motion.div>
          <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <button
              onClick={() => setShowFlyer(true)}
              className="w-full cursor-pointer border border-transparent transition-all hover:border-[#FFBD00]/50 focus:outline-none"
              aria-label="Enlarge flyer"
              data-testid="hero-flyer-button"
            >
              <img
                src="/img/flyer.jpg"
                alt="The Sessions Project — official event flyer"
                className="w-full"
                data-testid="hero-flyer-image"
              />
            </button>
          </motion.div>
        </div>
        <button onClick={scrollToRsvp} aria-label="Scroll to RSVP" className="mx-auto mt-16 flex animate-bounce items-center justify-center text-[#555] hover:text-white" data-testid="scroll-down-indicator">
          <ArrowDown size={20} />
        </button>
      </section>

      {/* Mosaic strip */}
      <MosaicStrip />

      {/* About */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <motion.div className="lg:col-span-5" {...fadeUp}>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: YELLOW }}>
              The Archive
            </div>
            <h2 className="mt-4 font-title text-3xl uppercase tracking-wide md:text-4xl">
              1600+ Photo-Sessions.
              <br />
              500+ Filmed Encounters.
            </h2>
          </motion.div>
          <motion.div className="space-y-6 text-base leading-relaxed text-[#A3A3A3] lg:col-span-7" {...fadeUp}>
            <p>
              The Session: a multi-camera filmed encounter, built to capture not just the finished image,
              but the moment of its making. The catalogue holds more than 1600 photo-sessions, over 500
              filmed Sessions and thousands of hours of evocative in-depth creative communication and
              spontaneous performance.
            </p>
            <p>
              For the first time ever, this extraordinary body of work is being brought to the public —{" "}
              <span className="text-white">The Power &amp; The Passion to Create Trilogy</span>: three
              feature-length documentary films built from Norman Seeff&apos;s unprecedented catalogue. A
              historical, forward-looking exploration of the inner creative dynamic and its outer
              expressions across the disciplines.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Event details */}
      <section className="border-t border-[#1A1A1A] bg-black/40">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12">
          <motion.div {...fadeUp}>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: YELLOW }}>
              The Evening
            </div>
            <h2 className="mt-4 font-title text-3xl uppercase tracking-wide md:text-4xl">
              Event Details
            </h2>
          </motion.div>
          <motion.div className="mt-12 grid grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-4" {...fadeUp}>
            <DetailItem icon={Calendar} label="When" value={"July 1st, 2026"} testId="detail-when" />
            <DetailItem icon={Clock} label="Time" value={"Program Starts at 8pm Sharp\nor No Admittance after 8pm"} testId="detail-time" />
            <DetailItem icon={MapPin} label="Where" value={"Yamashiro, Hollywood\n1999 N. Sycamore Ave\nLA, CA 90068"} testId="detail-where" />
            <DetailItem icon={Sparkles} label="Featuring" value={"Screening · Cocktails\nSushi · Curated Culinary\nSpecial Guests"} testId="detail-featuring" />
          </motion.div>
          <motion.p className="mx-auto mt-20 max-w-3xl text-center font-title text-xl uppercase leading-relaxed tracking-wide md:text-2xl" {...fadeUp}>
            What do we discover when we observe the great creators functioning at the higher reaches of
            human creativity? <span style={{ color: YELLOW }}>The journey begins…</span>
          </motion.p>
        </div>
      </section>

      {/* RSVP */}
      <section id="rsvp" className="border-t border-[#1A1A1A]">
        <div className="mx-auto max-w-7xl px-6 py-24 text-center md:px-12">
          <motion.div {...fadeUp}>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: YELLOW }}>
              By Invitation Only
            </div>
            <h2 className="mt-4 font-title text-4xl uppercase tracking-wide md:text-5xl">
              {ticket ? "See You There" : "Reserve Your Place"}
            </h2>
            {!ticket && (
              <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-[#A3A3A3]">
                Guests will enjoy an exclusive preview of archival and current Sessions media content and a
                presentation of The Power &amp; the Passion to Create by Norman Seeff.
              </p>
            )}
          </motion.div>
          <div className="mt-14">
            {ticket ? <Ticket ticket={ticket} /> : <RsvpForm onSuccess={setTicket} />}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1A1A1A] bg-black/60 px-6 py-10 text-center md:px-12">
        <div className="font-title text-sm uppercase tracking-[0.3em]">
          Norman Seeff — The Sessions Project
        </div>
        <a href="mailto:RSVP@NORMANEVENT.COM" className="mt-3 inline-block text-xs font-bold uppercase tracking-[0.25em] hover:underline" style={{ color: YELLOW }} data-testid="footer-rsvp-email">
          RSVP@NORMANEVENT.COM
        </a>
      </footer>
      </div>

      {showFlyer && (
        <FlyerModal
          src="/img/flyer.jpg"
          alt="The Sessions Project — official event flyer"
          onClose={() => setShowFlyer(false)}
        />
      )}
    </div>
  );
}
